import Logo from "../images/AE-Logo.svg";
import { Link as ScrollLink } from "react-scroll";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as fa from "react-icons/fa";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useQueryClient } from '@tanstack/react-query';
import Modal from "./Modal"; // Import the Modal component
import AlertMessage from "./AlertMessage"; // Import the AlertMessage component

// Helper to read any cookie by name
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mobileNavActive, setMobileNavActive] = useState(false);
  const [dropdownActive, setDropdownActive] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false); // State for logout modal
  const [isLoggingOut, setIsLoggingOut] = useState(false); // Track logout in progress
  const [alert, setAlert] = useState({ show: false, type: 'success', message: '' }); // State for alert messages
  const navRef = useRef(null);

  // Check if we're on the home page to use ScrollLink, otherwise use regular Link
  const isHomePage = location.pathname === '/';

  // Check for auth token on mount and when location changes
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    setIsLoggedIn(!!token); // Convert to boolean
  }, [location]); // Re-check when location changes

  // Toggle mobile menu
  const toggleMobileNav = () => {
    setMobileNavActive(!mobileNavActive);
    if (dropdownActive) setDropdownActive(null);
  };

  // Toggle dropdown
  const toggleDropdown = (index) => {
    if (dropdownActive === index) {
      setDropdownActive(null);
    } else {
      setDropdownActive(index);
    }
  };

  // Handle logout confirmation
  const handleLogoutClick = (e) => {
    e.preventDefault(); // Prevent navigation
    setShowLogoutModal(true);
    setMobileNavActive(false); // Close mobile menu when opening modal
  };

  // Perform actual logout
  const confirmLogout = async () => {
    setIsLoggingOut(true);

    try {
      // Attempt to call logout API (best-effort — we always clean up client side)
      try {
        const csrfToken = getCookie('csrftoken');
        await axios.post(`${process.env.REACT_APP_API_URL}/rest-auth/logout/`, {}, {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            ...(csrfToken && { 'X-CSRFToken': csrfToken }),
          }
        });
      } catch (apiError) {
      }

      // Clear ALL auth-related localStorage keys (mirrors Topbar exactly)
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('username');
      localStorage.removeItem('userId');
      localStorage.removeItem('is_superuser');
      localStorage.removeItem('authTokenStoredAt');
      localStorage.removeItem('quoteFormData');

      // Clear ALL React Query cached data so no stale user/dashboard data leaks
      queryClient.clear();

      // Update state
      setIsLoggedIn(false);
      setShowLogoutModal(false);
      setDropdownActive(null);

      // Show success message
      setAlert({
        show: true,
        type: 'success',
        message: 'You have been successfully logged out!'
      });

      // Navigate to home if not already there
      if (location.pathname !== '/') {
        navigate('/');
      }
    } catch (error) {

      // Still clear everything on unexpected failure
      localStorage.clear();
      queryClient.clear();

      setShowLogoutModal(false);
      setIsLoggedIn(false);

      setAlert({
        show: true,
        type: 'error',
        message: error.response?.data?.detail || 'Failed to logout. Please try again.'
      });
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Cancel logout
  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  // Close mobile menu when clicking on a link
  const handleLinkClick = () => {
    setMobileNavActive(false);
    setDropdownActive(null);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        // Check if click is not on mobile-nav-toggle
        if (!event.target.closest('.mobile-nav-toggle')) {
          setMobileNavActive(false);
          setDropdownActive(null);
        }
      }
    };

    // Add body class when mobile nav is active
    if (mobileNavActive) {
      document.body.classList.add('mobile-nav-active');
    } else {
      document.body.classList.remove('mobile-nav-active');
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.classList.remove('mobile-nav-active');
    };
  }, [mobileNavActive]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileNavActive(false);
    setDropdownActive(null);
  }, [location]);

  // Auto-hide alert after 5 seconds
  useEffect(() => {
    if (alert.show) {
      const timer = setTimeout(() => {
        setAlert({ ...alert, show: false });
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  // Navigation items data
  const navItems = [
    { id: 'hero', label: 'Home', to: '/', scrollTo: 'hero' },
    { id: 'about', label: 'About Us', to: '/#about', scrollTo: 'about' },
    { id: 'services', label: 'Our Services', to: '/#services', scrollTo: 'services' },
  ];

  return (
    <>
      <header
        id="header"
        className="header flex items-center fixed top-0 left-0 right-0 z-50 bg-white shadow-md w-full py-4"
      >
        <div className="container-fluid container relative flex items-center justify-between w-full px-4 md:px-6 mx-auto">

          <Link to="/" className="logo flex items-center" onClick={handleLinkClick}>
            <img
              src={Logo}
              alt="Alternative Energy"
              className="h-12 w-auto md:h-16 lg:h-20"
            />
          </Link>

          <nav
            id="navmenu"
            className="navmenu"
            ref={navRef}
          >
            <ul className={mobileNavActive ? 'block' : 'hidden'}>
              {navItems.map((item) => (
                <li key={item.id}>
                  {isHomePage ? (
                    <ScrollLink
                      to={item.scrollTo}
                      smooth={true}
                      duration={500}
                      spy={true}
                      activeClass="active"
                      className="cursor-pointer"
                      onClick={handleLinkClick}
                    >
                      {item.label}
                    </ScrollLink>
                  ) : (
                    <Link to={item.to} onClick={handleLinkClick}>
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}

              <li className="dropdown">
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleDropdown('main');
                  }}
                >
                  <span>More</span>
                  <i className={`bi bi-chevron-down toggle-dropdown ${dropdownActive === 'main' ? 'rotate-180' : ''
                    }`}></i>
                </Link>
                <ul className={dropdownActive === 'main' ? 'dropdown-active' : ''}>
                  {/* More Services */}
                  <li>
                    {isHomePage ? (
                      <ScrollLink
                        to="services-2"
                        smooth={true}
                        duration={500}
                        spy={true}
                        activeClass="active"
                        className="cursor-pointer"
                        onClick={handleLinkClick}
                      >
                        More Services
                      </ScrollLink>
                    ) : (
                      <Link to="/#services-2" onClick={handleLinkClick}>More Services</Link>
                    )}
                  </li>
                  {/* Recent Projects */}
                  <li>
                    {isHomePage ? (
                      <ScrollLink
                        to="recent-posts"
                        smooth={true}
                        duration={500}
                        spy={true}
                        activeClass="active"
                        className="cursor-pointer"
                        onClick={handleLinkClick}
                      >
                        Recent Projects
                      </ScrollLink>
                    ) : (
                      <Link to="/#recent-posts" onClick={handleLinkClick}>Recent Projects</Link>
                    )}
                  </li>
                  {/* Contact */}
                  <li>
                    {isHomePage ? (
                      <ScrollLink
                        to="contact"
                        smooth={true}
                        duration={500}
                        spy={true}
                        activeClass="active"
                        className="cursor-pointer"
                        onClick={handleLinkClick}
                      >
                        Contact
                      </ScrollLink>
                    ) : (
                      <Link to="/#contact" onClick={handleLinkClick}>Contact</Link>
                    )}
                  </li>
                  {/* Get Quotation */}
                  <li>
                    <Link to="/quote" onClick={handleLinkClick}>Get Quotation</Link>
                  </li>
                </ul>
              </li>

              {/* Dashboard Link - Only shown when logged in */}
              {isLoggedIn && (
                <li>
                  <Link
                    to="/dashboard"
                    className={location.pathname === '/dashboard' ? 'active' : ''}
                    onClick={handleLinkClick}
                  >
                    Dashboard
                  </Link>
                </li>
              )}

              {/* Login/Logout Link */}
              <li>
                {isLoggedIn ? (
                  <Link
                    to="#"
                    onClick={handleLogoutClick}
                    className="logout-link"
                  >
                    Logout
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    className={location.pathname === '/login' ? 'active' : ''}
                    onClick={handleLinkClick}
                  >
                    Log in
                  </Link>
                )}
              </li>
            </ul>

            <i
              className="mobile-nav-toggle flex xl:hidden bi bi-list cursor-pointer"
              onClick={toggleMobileNav}
            >
              <fa.FaBars className={mobileNavActive ? 'text-white' : ''} />
            </i>
          </nav>

        </div>
      </header>

      {/* Alert Message */}
      {alert.show && (
        <AlertMessage
          type={alert.type}
          message={alert.message}
          duration={2500}
          onDismiss={() => setAlert({ ...alert, show: false })}
          showCloseButton={true}
        />
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <Modal
          title="Confirm Logout"
          message="Are you sure you want to logout?"
          onClick={cancelLogout}
          altButton={
            <button
              onClick={confirmLogout}
              type="button"
              disabled={isLoggingOut}
              className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoggingOut ? 'Logging out...' : 'Logout'}
            </button>
          }
        />
      )}
    </>
  );
};

export default Navbar;